<template>
  <div v-if="doc" data-test="personal-pathway-wheel">
    <div class="container">
      <a
        href="/home?view=MyDashboard"
        role="button"
        class="btn btn-default-back back-button"
        data-test="ppw-back-btn"
      >
        My Dashboard
      </a>
      <PathwayWheelHeader :currentOutcome="currentOutcome" />
    </div>
    <div v-if="model && isReady" class="cms-session-wheel" data-test="ppw-wheel">
      <SessionWheel :wheelColour="wheelColour" :model="model" :includeBackButton="true" />
    </div>
  </div>
  <LoadingPage v-else />
</template>

<script setup lang="ts">
import { computed, ref, watchEffect } from "vue";
import SessionWheel, { CmsSessionApiModel } from "../sessions/sessions-wheel.vue";
import {
  CmsDocument,
  CmsGoalData,
  CmsPersonalPathway,
  CmsPersonalPathwayStep,
} from "../../services/cms/cms-type-definitions";
import cms from "../../services/cms/cms";
import { PersonalPathwayCompletedStepApiModel, SessionsApiModel } from "../../api-models";
import { useAxios } from "../../composables/api";
import PathwayWheelHeader from "../../components/cms/pathway-wheel-header.vue";
import LoadingPage from "../loading-page.vue";
import { settleAll } from "../../services/utilities/settle-all";

const props = defineProps<{ doc: CmsDocument }>();
const doc = ref<CmsPersonalPathway>(props.doc as CmsPersonalPathway);

const userProgress = ref<(PersonalPathwayCompletedStepApiModel | null | undefined)[]>([]);

const isReady = ref(false);

const wheelColour = computed<string>(() => {
  return doc.value.data?.goal_relationship.data?.goal_colour || "#B0BB36";
});

const currentOutcome = computed<Partial<CmsGoalData> | null>(() => {
  if (doc.value.data.goal_relationship.data) {
    return doc.value.data.goal_relationship.data;
  }
  return null;
});

watchEffect(async () => {
  const stepProgress = doc.value.data.pathway_steps.map(
    () => undefined as PersonalPathwayCompletedStepApiModel | null | undefined,
  );

  const progressPromises: Promise<PersonalPathwayCompletedStepApiModel>[] = [];

  doc.value.data.pathway_steps.forEach((step) => {
    const { promise } = useAxios<PersonalPathwayCompletedStepApiModel>(
      `/api/personalcompletedpathwaystep?cmsStepId=${step.pathway_step.id}&cmsPathwayId=${doc.value.id}`,
    );
    progressPromises.push(promise);
  });
  settleAll(progressPromises).then((pp) => {
    pp.forEach((p, index) => {
      stepProgress[index] = p.value;
    });
    userProgress.value = stepProgress;
    isReady.value = true;
  });
});

const model = computed<SessionsApiModel | null>(() => {
  if (!userProgress.value || !userProgress.value?.every((p) => p !== undefined)) {
    return null;
  }

  const sessions = doc.value.data.pathway_steps.map((step, index): CmsSessionApiModel => {
    const pathwayStep: CmsPersonalPathwayStep = step.pathway_step as any;
    const isCompleted = !!userProgress.value[index];

    return {
      id: pathwayStep.id,
      index,
      shortTitle: cms.asText(pathwayStep.data?.tile_title) || cms.asText(pathwayStep.data.title),
      fullTitle: cms.asText(pathwayStep.data.title),
      availableDescription: [],
      unavailableDescription: [],
      theme: "team-theme",
      availableDescriptionFromCms: step.step_description,
      unavailableDescriptionFromCms: step.step_description,
      durationDescription: "",
      callToAction: "",
      isAvailable: true,
      isCompleted,
      latestSceneIndex: 0,
      imageUrl: doc.value.data?.title_image?.url || pathwayStep.data.title_image.url,
      url: cms.linkResolver(pathwayStep),
      buttonText: cms.asText(step.step_button_text) || "Go",
      itemTypeText: cms.asText(step.step_type_text) || "Step",
    };
  });
  return { sessions };
});
</script>

<style scoped lang="less">
@import "../../styles/outcomes/pathway-wheel";
</style>
